import styled from "@emotion/styled";
import { useConnection } from "@solana/wallet-adapter-react";
import type { PublicKey } from "@solana/web3.js";
import { StepTextInput } from "@stepfinance/react";
import type { FC } from "react";
import { useEffect, useState } from "react";

import { useLanguageContext } from "../../contexts/language/language";
import { useSendMetaplexAsset } from "../../hooks/mutations/metaplex/useSendMetaplexAsset";
import {
  type CompressedNFT,
  isCompressedNFT,
  isNFTWithTokenAccount,
  type NFTWithTokenAccount,
} from "../../models/nfts/nfts";
import { validateAddressInput } from "../../utils/utils";
import { AmountInput } from "../AmountInput/AmountInput";
import { NFTFileDisplay } from "../nft/NFTFileDisplay";
import { ModalImageContainer } from "../shared";
import { TradeButton } from "../Trade";
import { ToAddressDiv } from "./shared";

interface SendNFTContentProps {
  nft: NFTWithTokenAccount | CompressedNFT;
  closeModal: () => void;
}

export const SendNFTContent: FC<SendNFTContentProps> = ({ nft, closeModal }: SendNFTContentProps) => {
  const { connection } = useConnection();

  const {
    currentLanguageData: {
      common,
      pages: {
        dashboard: {
          modules: {
            balances: { sendTokenContent, sendButton },
          },
        },
      },
    },
  } = useLanguageContext();

  const sendNFTMutation = useSendMetaplexAsset();

  const [sendAmount, setSendAmount] = useState("");
  const [toAddressText, setToAddressText] = useState("");
  const [toAddress, setToAddress] = useState<PublicKey | null>(null);

  const onAddressChange = (e) => {
    setToAddressText(e.target.value);
    void validateAddressInput(e.target.value, connection).then(setToAddress);
  };

  useEffect(() => {
    const balance = nft.amount;
    setSendAmount(balance.toString());
  }, [nft.amount]);

  const sendMint = nft.address;

  return (
    <>
      <ModalImageContainer>
        <NFTFileDisplay nft={nft} />
      </ModalImageContainer>
      <AmountInput
        tokenInfoDisplay={`${nft.name} (Metaplex)`}
        showBalance={false}
        amount={sendAmount}
        mint={sendMint}
        mintDecimals={0} // Always zero for NFTs
        onAmountChange={(event) => {
          setSendAmount(event.currentTarget.value);
        }}
        priceOverride={nft.floorPriceUSD}
        onlyAllowIntegers
      />
      <ToAddressDiv>{sendTokenContent.toAddress}</ToAddressDiv>
      <AdressInputContainer>
        <StepTextInput
          value={toAddressText}
          spellCheck={false}
          inputSize="small"
          borderless
          style={{ backgroundColor: "black" }}
          placeholder={sendTokenContent.addressInputPlaceholder}
          onChange={onAddressChange}
        />
      </AdressInputContainer>
      <TradeButton
        disabled={!toAddress}
        onClick={() => {
          if (toAddress) {
            if (isNFTWithTokenAccount(nft) || isCompressedNFT(nft)) {
              sendNFTMutation.mutate({ nft, amount: Number(sendAmount), toAddress: toAddress.toBase58() });
            }
            closeModal();
          }
        }}
      >
        {toAddress ? sendButton : common.stepButtonLabels.enterAddress}
      </TradeButton>
    </>
  );
};

const AdressInputContainer = styled.div`
  margin-bottom: 30px;
`;
